// ════════════════════════════════════════════════════════════════════════════
// UiState — the Class-B "restorable view state" schema (device-local).
//
// Phase 1 of plans/user-ui-preferences-state.md (#1566): the typed contract for
// the frontend `uiStateStore` (localStorage key `va-ui-state-v1`). Co-located in
// shared/ (not frontend/) so the Phase 2+ backend (`PUT /settings/ui-state`,
// cross-device resume subset) reuses the SAME type — per the plan §7 Phase 1.
//
// Classification discipline (plan §2): every key here is Class B ("where I was"
// on THIS device — annoying to lose on reload). Class A (cross-device durable
// prefs) stays in `UserPreferences`; Class C (ephemeral interaction state —
// open menus, drags, toasts, confirm dialogs) must NEVER appear here.
//
// Adding a key? Follow the runbook in plan §6: type it here, add the default,
// whitelist it in uiStateStore's `partialize`, read/write through the store
// (named action), add an SE in setpoint-evals/ui-state/.
// ════════════════════════════════════════════════════════════════════════════

/**
 * Top-level view. Post agents-drawer-carousel-reorg Phase 2, chat IS home and
 * 'list' is vestigial (the session list became the Agents drawer) — the union is
 * kept so the store remains the single owner of the historical
 * `voice-assistant-active-view` semantic it migrated in (R-A4).
 */
export type UiActiveView = 'list' | 'chat';

/** Session-list drawer tabs (content-scoped set — Sessions/Plans/Roads). */
export type UiSessionListTab = 'sessions' | 'plans' | 'roads';

/** Session-list sort order (Phase 2). */
export type UiSessionListSort = 'recent' | 'name';

/** Plans tab status filter (Phase 2) — mirrors PlansTab's StatusFilter union. */
export type UiPlansStatusFilter = 'all' | 'active' | 'executing' | 'stale';

/** Tasks (WorkItems) panel kind/state filters (Phase 2) — mirror WiKind/WiState. */
export type UiWorkItemKind = 'both' | 'issue' | 'pr';
export type UiWorkItemState = 'open' | 'closed' | 'all';

/** Class-B restorable view state persisted device-locally (`va-ui-state-v1`). */
export interface UiState {
  /** Which top-level view the operator was in (chat is home). */
  activeView: UiActiveView;
  /** Which tab the session-list (Agents) drawer was on. */
  sessionListTab: UiSessionListTab;
  /** Was the unified file drawer (drawerStore panel==='files') open? */
  fileDrawerOpen: boolean;

  // ── Phase 2: session list (sort + filters + scroll) ──────────────────────
  /** Session-list sort order. */
  sessionListSort: UiSessionListSort;
  /** 'You' filter — only sessions with in-progress turns. */
  sessionListShowInProgress: boolean;
  /** 'Agent' filter — only autonomous sessions. */
  sessionListShowAutonomous: boolean;
  /** Linked-issue filter (sessions for GitHub issue #N); null = off. */
  sessionListIssueFilter: number | null;
  /** Vertical scroll offset (px) of the session list. */
  sessionListScroll: number;

  // ── Phase 2: tab filters (Plans status, Tasks repos/kind/state) ──────────
  /** Plans tab status filter. */
  plansStatusFilter: UiPlansStatusFilter;
  /** Tasks (WorkItems) kind filter. */
  workItemsKind: UiWorkItemKind;
  /** Tasks (WorkItems) state filter. */
  workItemsState: UiWorkItemState;
  /** Tasks (WorkItems) selected repos ([] = not chosen yet → select-all on first load). */
  workItemsRepos: string[];

  // ── Phase 2: file drawer (sections + scroll + upload target) ─────────────
  /** Workspace-centric mode: per-project group collapsed flags. */
  fileDrawerCollapsedProjects: Record<string, boolean>;
  /** Workspace-centric mode: per-section collapsed flags. */
  fileDrawerCollapsedSections: Record<string, boolean>;
  /** Multi-section mode: per-section open flags (absent = default open). */
  fileDrawerOpenSections: Record<string, boolean>;
  /** Workspace-centric mode: drawer scroll offset (px). */
  fileDrawerScroll: number;
  /** Last upload-target project ('' = none yet). */
  fileDrawerUploadProject: string;
}

export const DEFAULT_UI_STATE: UiState = {
  activeView: 'chat',
  sessionListTab: 'sessions',
  fileDrawerOpen: false,
  sessionListSort: 'recent',
  sessionListShowInProgress: false,
  sessionListShowAutonomous: false,
  sessionListIssueFilter: null,
  sessionListScroll: 0,
  plansStatusFilter: 'all',
  workItemsKind: 'both',
  workItemsState: 'open',
  workItemsRepos: [],
  fileDrawerCollapsedProjects: {},
  fileDrawerCollapsedSections: {},
  fileDrawerOpenSections: {},
  fileDrawerScroll: 0,
  fileDrawerUploadProject: '',
};

/**
 * The persistence whitelist — exactly the UiState keys. uiStateStore's
 * `partialize` and `migrate` iterate THIS list, so an unknown/removed key can
 * never survive a rehydrate (rename-safe restore, plan §3 principle 5).
 */
export const UI_STATE_KEYS = Object.keys(DEFAULT_UI_STATE) as Array<keyof UiState>;
